import { useRef, useState, useLayoutEffect } from "react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";

import { SignInResult } from "../../state/actions/authActions";
import { SsoProviders } from "../../ajax/UserAccountAjax";
import { Button } from "../../components/Buttons/Button";
import { useSsoSignIn } from "./useSsoSignIn";
import { LinkedInLogo, MicrosoftLogo } from "./ProviderLogos";

type Props = {
  providers: SsoProviders;
  disabled: boolean;
  onStart: () => void;
  onResult: (result: SignInResult) => void;
};

const SsoButtons: React.FC<Props> = ({ providers, disabled, onStart, onResult }) => {
  const { google, microsoft, linkedIn } = providers;
  const { signInWithMicrosoft, signInWithLinkedIn, signInWithGoogle, googleFailed } = useSsoSignIn(providers, onStart, onResult);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [googleButtonWidth, setGoogleButtonWidth] = useState(300);

  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) =>
      setGoogleButtonWidth(Math.min(400, Math.round(entry.contentRect.width))));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={wrapperRef} className="flex flex-col gap-2 max-w-[400px] w-full mx-auto">
      {google && (
        <GoogleOAuthProvider clientId={google.clientId}>
          <div className={disabled ? "pointer-events-none opacity-50" : undefined}>
            <GoogleLogin
              onSuccess={signInWithGoogle}
              onError={googleFailed}
              width={googleButtonWidth}
              shape="rectangular"
              text="continue_with"
            />
          </div>
        </GoogleOAuthProvider>
      )}
      {microsoft && (
        <Button variant="secondary" fullWidth disabled={disabled} onClick={signInWithMicrosoft}>
          <MicrosoftLogo />
          Continue with Microsoft
        </Button>
      )}
      {linkedIn && (
        <Button variant="secondary" fullWidth disabled={disabled} onClick={signInWithLinkedIn}>
          <LinkedInLogo />
          Continue with LinkedIn
        </Button>
      )}
    </div>
  );
};

export default SsoButtons;
